import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ResourceGroupDisplayComponent } from '@app/directives/_layout/resourceGroupDisplay/resourceGroupDisplay.component';
import { IVTableCell } from '@app/directives/_layout/v-table/_models/IVTableCell';
import { IVTableColumn } from '@app/directives/_layout/v-table/_models/IVTableColumn';
import { IVTableRow } from '@app/directives/_layout/v-table/_models/IVTableRow';
import { VTableService } from '@app/directives/_layout/v-table/_services/v-table.service';
import { FilterBarComponent } from '@app/directives/_resourceFilters/filter-bar/filter-bar.component';
import { VehicleStatusComponent } from '@app/directives/_vehicle/vehicle-status/vehicle-status.component';
import { ResourceType } from '@app/enum/ResourceType';
import { VehicleType } from '@app/enum/VehicleType.enum';
import { VTableSelectionType } from '@app/enum/VTableSelectionType';
import { IDriver } from '@app/interfaces/IDriver';
import { IGeofence } from '@app/interfaces/IGeofences';
import { IVehicle } from '@app/interfaces/IVehicle';
import { GroupService } from '@app/services/groups/group.service';
import { TranslationService } from '@app/services/translation.service';
import { IGroupWithIds } from '../../_models/IGroupWithIds';
import './group-resources.component.scss';

@Component({
    selector: 'group-resources',
    template: require('./group-resources.component.html')
})
export class GroupResourcesComponent implements OnInit, OnChanges{
    private groups: IGroupWithIds[] = [];

    @Input() resources: IVehicle[] | IDriver[] | IGeofence[] = [];
    @Input() resourceType: ResourceType;
    @Input() isDepot: boolean;

    @Input() group: IGroupWithIds;
    @Output() groupChange = new EventEmitter<IGroupWithIds>();

    @ViewChild(FilterBarComponent) filterBar: FilterBarComponent; 
    
    tableColumns: IVTableColumn<IVehicle | IDriver | IGeofence>[] = [];
    tableRows: IVTableRow<IGroupWithIds>[] = [];

    VTableSelectionType = VTableSelectionType;
    ResourceType = ResourceType;

    constructor(
        private readonly translationService: TranslationService,
        private readonly vTableService: VTableService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly groupService: GroupService ) { }

    async ngOnInit(): Promise<void> {
        this.groups = await this.groupService.getGroupsWithIds();
        this.createTable();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const isDepotChange = !!changes['isDepot'];
        const resourcesChange = changes['resources'];
        const groupChange = !!changes['group'];
        const resourcesUpdated = resourcesChange?.currentValue?.length;

        const shouldUpdate = 
            groupChange || 
            isDepotChange || 
            (resourcesChange && this.resources?.length && this.tableColumns && resourcesUpdated);

        if (shouldUpdate) {
            this.createTableRows();
            this.selectResourcesInGroup();
        }
    }

    onSelectedRowsChange(selectedRows: IVTableRow<IGroupWithIds>[]): void {
        const ids = new Set(selectedRows.map(row => row.data.Id));
        this.assignResourcesToGroup([...ids]);
        this.filterBar.updateTabCounts();
    }

    private createTable(): void {
        this.tableColumns = [];

        this.tableColumns.push({
            header: this.vTableService.createTableHeader({ tKey: "groups.Label", width: this.getWidthForLabel() }),
            createCellFunction: resource => this.getLabelCellForResource(resource)
        })

        if (this.resourceType == ResourceType.Vehicle) {
            this.tableColumns.push({
                header: this.vTableService.createTableHeader({ tKey: 'Common.Type', width: 150 }),
                createCellFunction: (resource: IVehicle) => this.vTableService.createEnumCell(VehicleType, "VehicleType", resource.VehicleType)
            })

            this.tableColumns.push({
                header: this.vTableService.createTableHeader({ tKey: 'Common.Status', width: 200, sortable: false, searchable: false }),
                createCellFunction: (resource: IVehicle) => this.vTableService.createTableCell(resource, {
                    component: VehicleStatusComponent,
                    componentInputs: {
                        vehicle: resource,
                        hideReg: true
                    },
                })
            })
        }

        if (this.resourceType !== ResourceType.Geofence) {
            this.tableColumns.push({
                header: this.vTableService.createTableHeader({ tKey: 'groupList.CurrentGroups' }),
                createCellFunction: resource => this.getCurrentGroupsCellForResource(resource)
            })
        }
    }

    private getWidthForLabel(): number {
        switch (this.resourceType) {
            case ResourceType.Driver:
                return 200;
            case ResourceType.Vehicle:
                return 100;
        }
    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.resources, this.tableColumns);
    }

    private getLabelCellForResource(resource: IVehicle | IDriver | IGeofence): IVTableCell {
        if (this.isVehicle(resource)) {
            return this.createLabelCellForVehicle(resource);
        }

        if (this.isDriver(resource)) {
            return this.createLabelCellForDriver(resource);
        }

        if (this.isGeofence(resource)) {
            return this.createLabelCellForGeofence(resource);
        }
    }

    private createLabelCellForVehicle(vehicle: IVehicle): IVTableCell {
        return this.vTableService.createVehicleRegCell(vehicle);
    }

    private createLabelCellForDriver(driver: IDriver): IVTableCell {
        return this.vTableService.createTableCell(driver.DisplayName);
    }

    private createLabelCellForGeofence(geofence: IGeofence): IVTableCell {
        return this.vTableService.createTableCell(geofence.Description);
    }

    private getCurrentGroupsCellForResource(resource: IVehicle | IDriver | IGeofence): IVTableCell {
        if (this.isVehicle(resource)) {
            return this.getCurrentGroupsCellForVehicle(resource);
        }

        if (this.isDriver(resource)) {
            return this.getCurrentGroupsCellForDriver(resource);
        }
    }

    private getCurrentGroupsCellForVehicle(vehicle: IVehicle): any {
        const depotContainingResource = this.getOtherDepotForResource(vehicle);
        const isDepot = this.checkForDepot();

        if (isDepot && !!depotContainingResource) {
            const vehicleIdInSelectedResources = this.group?.VehicleIds?.includes(vehicle.Id);
            const groupContainsVehicle = this.group.VehicleIds.includes(vehicle.Id);

            if (vehicleIdInSelectedResources || (!this.group?.VehicleIds && groupContainsVehicle)) {
                return this.vTableService.createTableCell(
                    this.translationService.t('groups.VehicleInOtherDepots', depotContainingResource.Name), 
                    {
                        tooltip: true,
                        classes: 'text-muted',
                        icon: 'fa-circle-exclamation'
                    }
                );
            }

            return this.vTableService.createTableCell(this.translationService.t('groups.cantSelectVehicles', depotContainingResource.Name), {
                disableRow: true,
                tooltip: true
            });

        }

        const searchAndSortString: string = this.getSearchAndSortStringForResourceGroups(vehicle, ResourceType.Vehicle);

        return this.vTableService.createTableCell(searchAndSortString, {
            component: ResourceGroupDisplayComponent,
            componentInputs: {
                groups: this.groups,
                vehicle: vehicle,
                max: 2
            }
        });
    }

    private getSearchAndSortStringForResourceGroups(resource: IVehicle, resourceType: ResourceType): string {
        const propertyName: string = `${ResourceType[resourceType]}Ids`;
        const resourceGroups: IGroupWithIds[] = this.groups.filter(group => group[propertyName].includes(resource.Id));
        return resourceGroups.map(group => group.Name).join();
    }


    private getCurrentGroupsCellForDriver(driver: IDriver): any {
        const searchAndSortString: string = this.getSearchAndSortStringForResourceGroups(driver, ResourceType.Driver);

        return this.vTableService.createTableCell(searchAndSortString, {
            component: ResourceGroupDisplayComponent,
            componentInputs: {
                groups: this.groups,
                driver: driver,
                max: 2
            }
        });
    }
    
    private selectResourcesInGroup(): void {
        if (!this.tableRows) {
            return;
        }

        this.tableRows.forEach(row => {
            row.selected = false;

            if (this.isVehicle(row.data) && this.group.VehicleIds.includes(row.data.Id)) {
                row.selected = true;
            }

            if (this.isDriver(row.data) && this.group.DriverIds.includes(row.data.Id)) {
                row.selected = true;
            }

            if (this.isGeofence(row.data) && this.group.GeofenceIds.includes(row.data.Id)) {
                row.selected = true;
            }
        });
    }

    private getOtherDepotForResource(resource: IVehicle): IGroupWithIds {
        let otherDepot: IGroupWithIds = null;

        const depots = this.groups.filter(group => group.IsDepot);

        for (let i = 0; i < depots.length; i++) {
            if (depots[i].Id === this.group.Id) {
                continue;
            }

            if (depots[i].VehicleIds.includes(resource.Id)) {
                otherDepot = depots[i];
                break;
            }
        }

        return otherDepot;
    }

    private checkForDepot(): boolean {
        let currentGroup = this.group;

        while (!!currentGroup) {
            if (currentGroup.IsDepot) {
                return true;
            }

            currentGroup = currentGroup.parent;
        }

        return false;
    }

    private isVehicle(resource: IVehicle | IGeofence | IDriver): boolean {
        return "Reg" in resource;
    }

    private isDriver(resource: IVehicle | IGeofence | IDriver): boolean {
        return "Username" in resource;
    }

    private isGeofence(resource: IVehicle | IGeofence | IDriver): boolean {
        return "Description" in resource;
    }
    
    private assignResourcesToGroup(ids: number[]): void {
        switch (this.resourceType) {
            case ResourceType.Vehicle:
                this.group.VehicleIds = ids;
                break;
            case ResourceType.Geofence:
                this.group.GeofenceIds = ids;
                break;
            case ResourceType.Driver:
                this.group.DriverIds = ids;
                break;
        }

        this.groupChange.emit(this.group);
    }
}
